import React, { useState, useRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { buildValueToLabel } from "./tacticModel";
import { QueryDisplay } from "./QueryDisplay";
import { QueryBuilderModal } from "./QueryBuilderModal";
import { AddStatementMenu } from "./AddStatementMenu";
import { ActionChip } from "./ActionChip";
import "./TacticEditor.css";

// Update an action statement's action + optional zone arg / call target.
// Dropping the unused one keeps a stale zone/target from lingering when the
// user switches action kind.
function setAction(statement, value, arg, callTarget) {
  const next = { ...statement, action: value };
  if (arg != null) next.arg = arg;
  else delete next.arg;
  if (callTarget != null) next.callTarget = callTarget;
  else delete next.callTarget;
  return next;
}

function QueryEditor({ query, queryTokens, syntaxTokens, zones, onUpdate }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <QueryDisplay query={query} queryTokens={queryTokens} syntaxTokens={syntaxTokens} zones={zones} onEdit={() => setOpen(true)} />
      {open && (
        <QueryBuilderModal
          query={query}
          queryTokens={queryTokens}
          syntaxTokens={syntaxTokens}
          zones={zones}
          onSave={(newQuery) => { onUpdate(newQuery); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function DeleteBtn({ onClick }) {
  return (
    <button className="delete-btn" onClick={onClick} title="Remove">
      ✕
    </button>
  );
}

function DragHandle({ listeners, attributes }) {
  return (
    <span className="drag-handle" {...listeners} {...attributes} title="Drag to reorder">
      ⠿
    </span>
  );
}

function SortableStatementBlock({ id, statement, tokens, depth, onUpdate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="sortable-item">
      <DragHandle listeners={listeners} attributes={attributes} />
      <StatementBlock
        statement={statement}
        tokens={tokens}
        depth={depth}
        onUpdate={onUpdate}
        onDelete={onDelete}
      />
    </div>
  );
}

/**
 * @param {{ statement: object, tokens: object, depth: number, onUpdate: (s: object) => void, onDelete: () => void }} props
 */
export function StatementBlock({ statement, tokens, depth = 0, onUpdate, onDelete }) {
  if (!tokens) return null;

  const { queries, actions, syntax, zones, callables } = tokens;
  const syntaxByValue = buildValueToLabel(syntax ?? {});
  const kwIf   = syntaxByValue[4] ?? "IF";
  const kwElse = syntaxByValue[5] ?? "ELSE";
  const indent = { paddingLeft: `${depth * 1.5}rem` };

  switch (statement.kind) {

    case "action":
      return (
        <div className="statement statement-row" style={indent}>
          <ActionChip
            value={statement.action}
            arg={statement.arg ?? null}
            callTarget={statement.callTarget ?? null}
            actions={actions}
            zones={zones}
            callables={callables}
            onSelect={(v, a, ct) => onUpdate(setAction(statement, v, a, ct))}
          />
          <DeleteBtn onClick={onDelete} />
        </div>
      );

    case "conditional_action":
      return (
        <div className="statement statement-row" style={indent}>
          <div className="statement-content">
            <QueryEditor query={statement.condition} queryTokens={queries} syntaxTokens={syntax} zones={zones} onUpdate={(q) => onUpdate({ ...statement, condition: q })} />
            <span className="arrow">→</span>
            <ActionChip
              value={statement.action}
              arg={statement.arg ?? null}
              callTarget={statement.callTarget ?? null}
              actions={actions}
              zones={zones}
              callables={callables}
              onSelect={(v, a, ct) => onUpdate(setAction(statement, v, a, ct))}
            />
          </div>
          <DeleteBtn onClick={onDelete} />
        </div>
      );

    case "if":
      return (
        <div className="statement if-statement">
          <div className="statement-row" style={indent}>
            <div className="if-header">
              <span className="keyword">{statement._elseIf ? `${kwElse} ${kwIf}` : kwIf}</span>
              <span className="paren">(</span>
              <QueryEditor query={statement.condition} queryTokens={queries} syntaxTokens={syntax} zones={zones} onUpdate={(q) => onUpdate({ ...statement, condition: q })} />
              <span className="paren">)</span>
              <span className="brace">{"{"}</span>
            </div>
            <DeleteBtn onClick={onDelete} />
          </div>
          <StatementList
            statements={statement.then}
            tokens={tokens}
            depth={depth + 1}
            onUpdate={(newThen) => onUpdate({ ...statement, then: newThen })}
          />
          <div className="brace-close" style={indent}>{"}"}</div>

          {statement.else ? (
            (() => {
              const elseIf = statement.else.length === 1 && statement.else[0].kind === "if"
                ? statement.else[0] : null;
              return elseIf ? (
                <StatementBlock
                  statement={{ ...elseIf, _elseIf: true }}
                  tokens={tokens}
                  depth={depth}
                  onUpdate={(updated) => onUpdate({ ...statement, else: [updated] })}
                  onDelete={() => onUpdate({ ...statement, else: elseIf.else })}
                  _elseIf
                />
              ) : (
                <>
                  <div className="statement-row" style={indent}>
                    <div className="else-header">
                      <span className="keyword">{kwElse}</span>
                      <span className="brace">{"{"}</span>
                    </div>
                    <DeleteBtn
                      onClick={() => onUpdate({ ...statement, else: undefined })}
                      title="Remove ELSE branch"
                    />
                  </div>
                  <StatementList
                    statements={statement.else}
                    tokens={tokens}
                    depth={depth + 1}
                    onUpdate={(newElse) => onUpdate({ ...statement, else: newElse })}
                  />
                  <div className="brace-close" style={indent}>{"}"}</div>
                </>
              );
            })()
          ) : (
            <div className="else-add-row" style={indent}>
              <button className="add-statement-btn"
                onClick={() => onUpdate({ ...statement, else: [] })}>
                + {kwElse}
              </button>
              <button className="add-statement-btn"
                onClick={() => onUpdate({ ...statement, else: [{ kind: "if", condition: { kind: "atom", value: 100 }, then: [] }] })}>
                + {kwElse} {kwIf}
              </button>
            </div>
          )}
        </div>
      );

    case "block":
      return (
        <div className="statement block-statement">
          <div className="statement-row" style={indent}>
            <span className="brace">{"{"}</span>
            <DeleteBtn onClick={onDelete} />
          </div>
          <StatementList
            statements={statement.statements}
            tokens={tokens}
            depth={depth + 1}
            onUpdate={(newStatements) => onUpdate({ ...statement, statements: newStatements })}
          />
          <div className="brace-close" style={indent}>{"}"}</div>
        </div>
      );

    default:
      return null;
  }
}

/**
 * @param {{ statements: object[], tokens: object, depth: number, onUpdate: (s: object[]) => void }} props
 */
export function StatementList({ statements, tokens, depth = 0, onUpdate }) {
  // Stable IDs survive re-renders; reset when list length changes by splicing the ref array.
  const idRef = useRef([]);
  if (idRef.current.length < statements.length) {
    let next = Math.max(...idRef.current, 0) + 1;
    while (idRef.current.length < statements.length) idRef.current.push(next++);
  } else if (idRef.current.length > statements.length) {
    idRef.current.length = statements.length;
  }
  const ids = idRef.current.slice(0, statements.length);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const handleAdd = (newStatement) => {
    idRef.current.push(Math.max(...idRef.current, 0) + 1);
    onUpdate([...statements, newStatement]);
  };

  const handleUpdate = (index, newStatement) => {
    onUpdate(statements.map((s, i) => (i === index ? newStatement : s)));
  };

  const handleDelete = (index) => {
    idRef.current.splice(index, 1);
    onUpdate(statements.filter((_, i) => i !== index));
  };

  const handleInsert = (index, newStatement) => {
    const newId = Math.max(...idRef.current, 0) + 1;
    idRef.current.splice(index, 0, newId);
    const next = [...statements];
    next.splice(index, 0, newStatement);
    onUpdate(next);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    idRef.current = arrayMove(idRef.current, oldIdx, newIdx);
    onUpdate(arrayMove(statements, oldIdx, newIdx));
  };

  const terminated = statements[statements.length - 1]?.kind === "action";

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="statement-list">
          {statements.map((s, i) => (
            <React.Fragment key={ids[i]}>
              {i > 0 && (
                <AddStatementMenu
                  onAdd={(stmt) => handleInsert(i, stmt)}
                  depth={depth}
                  insert
                />
              )}
              <SortableStatementBlock
                id={ids[i]}
                statement={s}
                tokens={tokens}
                depth={depth}
                onUpdate={(newStatement) => handleUpdate(i, newStatement)}
                onDelete={() => handleDelete(i)}
              />
            </React.Fragment>
          ))}
          {statements.length === 0 && <AddStatementMenu onAdd={handleAdd} depth={depth} />}
          {statements.length > 0 && !terminated && <AddStatementMenu onAdd={handleAdd} depth={depth} insert />}
        </div>
      </SortableContext>
    </DndContext>
  );
}
